import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const inputCls =
  "w-full h-12 rounded-md bg-white border border-slate-200 px-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/20 focus-visible:border-blue-600";

// Reusable location input with debounced OpenStreetMap (Nominatim) suggestions.
export const LocationInput = ({ value, onChange, onCoords, placeholder, testid }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const skipFetch = useRef(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/geocode/search`, {
          params: { q: value },
        });
        setSuggestions(data.results || []);
        setOpen((data.results || []).length > 0);
        setActive(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const pick = (s) => {
    skipFetch.current = true;
    onChange(s.label);
    onCoords?.([s.lat, s.lon]);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          data-testid={testid}
          className={`${inputCls} pr-9`}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            onChange(e.target.value);
            onCoords?.(null);
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
        </span>
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-[1000] mt-1 w-full max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1"
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              data-testid={`${testid}-option-${i}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              className={`px-3 py-2 text-sm cursor-pointer flex gap-2 items-start transition-colors duration-150 ${
                i === active ? "bg-blue-50 text-blue-900" : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
              <span className="leading-snug">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
